import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight } from "react-icons/fi";
import { useInView } from "../components/useInView";
import "./Services.css";

/* ── Static images that are NOT from the DB ── */
import heroBg from "../assets/uploads/gallery/1771694350812-68069707.jpg";
import introImg  from "../assets/uploads/gallery/1771447596586-515389595.jpg";
const processImg = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=900&q=80";

/* ── Values section: .webp images from src/assets/uploads/about ── */
import valImgOil          from "../assets/uploads/about/oil.webp";
import valImgDiagnostic   from "../assets/uploads/about/diagnostic.webp";
import valImgEngine       from "../assets/uploads/about/engine.webp";
import valImgBrakes       from "../assets/uploads/about/brakes.webp";
import valImgTransmission from "../assets/uploads/about/transmissions.webp";
import valImgTires        from "../assets/uploads/about/tires_wheels.webp";

import svcIcon01 from "../assets/uploads/services/service_01.svg";
import svcIcon02 from "../assets/uploads/services/service_02.svg";
import svcIcon03 from "../assets/uploads/services/service_03.svg";
import svcIcon04 from "../assets/uploads/services/service_04.svg";
import svcIcon05 from "../assets/uploads/services/service_05.svg";
import svcIcon06 from "../assets/uploads/services/service_06.svg";
import svcIcon07 from "../assets/uploads/services/service_07.svg";
import svcIcon08 from "../assets/uploads/services/service_08.svg";
import svcIcon09 from "../assets/uploads/services/service_09.svg";
import svcIcon10 from "../assets/uploads/services/service_10.svg";
import svcIcon11 from "../assets/uploads/services/service_11.svg";
import svcIcon12 from "../assets/uploads/services/service_12.svg";

import shopImg from "../assets/uploads/about/shop_here.png"; 


/* Ordered array — index 0 → service_01.svg, index 11 → service_12.svg */
const svcIcons = [
  svcIcon01, svcIcon02, svcIcon03, svcIcon04,
  svcIcon05, svcIcon06, svcIcon07, svcIcon08,
  svcIcon09, svcIcon10, svcIcon11, svcIcon12,
];

/* ─────────────────────────────────────
   Static data
   ───────────────────────────────────── */

/* 6 values — use imported .webp images */
const values = [
  {
    img:   valImgOil,
    title: "Oil Changes",
    desc:  "Vehicle maintenance is extremely important and the easiest thing a vehicle owner can do is get the oil changed.",
  },
  {
    img:   valImgDiagnostic,
    title: "Diagnostic",
    desc:  "Our certified technicians use the latest diagnostic equipment to service and repair your vehicles.",
  },
  {
    img:   valImgEngine,
    title: "Engines",
    desc:  "We use state-of-the-art scan tool equipment to perform engine diagnostics and repair.",
  },
  {
    img:   valImgBrakes,
    title: "Brakes",
    desc:  "If you have any doubts regarding the current condition of your brakes, it's best to call or pay a visit to us.",
  },
  {
    img:   valImgTransmission,
    title: "Transmissions",
    desc:  "If you want your car to be in top shape, you have to take good care of its transmission system.",
  },
  {
    img:   valImgTires,
    title: "Tires & Wheels",
    desc:  "Our wheel alignment service will give you the best driving experience.",
  },
];

const steps = [
  {
    n: "STEP 1",
    title: "Initial Consultation & Discovery",
    desc: "We start by understanding your vision, goals, and project requirements — laying the groundwork for a successful build.",
  },
  {
    n: "STEP 2",
    title: "Strategic Planning & Design",
    desc: "Our team creates detailed plans, timelines, and cost estimates, ensuring everything is aligned before construction begins.",
  },
  {
    n: "STEP 3",
    title: "Construction & Execution",
    desc: "With skilled teams and strict supervision, we bring the design to life — on time, on budget, and to the highest standards.",
  },
  {
    n: "STEP 4",
    title: "Final Handover & Follow-Up",
    desc: "We complete final inspections, walk you through the results, and remain available for any post-build support you need.",
  },
];

/* ─────────────────────────────────────
   Helpers
   ───────────────────────────────────── */

/* Disable right-click on the page */
function useNoContextMenu() {
  useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);
}

/* Animated counter */
function Counter({ to, suffix = "", delay = 0 }) {
  const [ref, inView] = useInView();
  const el = useRef(null);
  useEffect(() => {
    if (!inView) return;
    let start = null;
    const run = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / 1800, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      if (el.current) el.current.textContent = Math.floor(eased * to);
      if (p < 1) requestAnimationFrame(run);
    };
    setTimeout(() => requestAnimationFrame(run), delay);
  }, [inView, to, delay]);
  return (
    <span ref={ref}>
      <span ref={el}>0</span>{suffix}
    </span>
  );
}

/* ── Pick the first available image from a service row ── */
function getServiceImage(svc) {
  const img = svc.image1 || svc.image2 || svc.image3 || svc.image4 || svc.image5;
  if (!img) return null;
  return `${process.env.REACT_APP_API_URL}/uploads/services/${img}`;
}

/* ── Strip all HTML tags and return plain text ── */
function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "")
    .replace(/\s+/g, " ")
    .trim();
}

/* ── Truncate plain text to N words, appending "…" if truncated ── */
function truncateWords(text, maxWords = 10) {
  const words = text.split(" ").filter(Boolean);
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(" ") + "…";
}

/* ── Combined helper used in the card ── */
function shortDesc(html, maxWords = 10) {
  return truncateWords(stripHtml(html), maxWords);
}

/* ─────────────────────────────────────
   Component
   ───────────────────────────────────── */
export default function Services() {
  useNoContextMenu();

  /* ── About-us data (for intro paragraph) ── */
  const [aboutData,    setAboutData]    = useState(null);
  const [loadingAbout, setLoadingAbout] = useState(true);

  /* ── Services from DB ── */
  const [services,   setServices]   = useState([]);
  const [svcLoading, setSvcLoading] = useState(true);
  const [svcError,   setSvcError]   = useState(null);

  const [heroRef,  heroInView]  = useInView();
  const [introRef, introInView] = useInView();
  const [valRef,   valInView]   = useInView();
  const [svcRef,   svcInView]   = useInView();
  const [procRef,  procInView]  = useInView();

  /* ── Fetch about-us ── */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/about-us`)
      .then(r => r.json())
      .then(data => setAboutData(data?.id ? data : null))
      .catch(err => console.error("About-us fetch failed:", err))
      .finally(() => setLoadingAbout(false));
  }, []);

  /* ── Fetch services from API ── */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/services`)
      .then(r => {
        if (!r.ok) throw new Error("Failed to fetch services");
        return r.json();
      })
      .then(data => {
        setServices(data);
        setSvcLoading(false);
      })
      .catch(err => {
        console.error(err);
        setSvcError("Could not load services. Please try again later.");
        setSvcLoading(false);
      });
  }, []);

  /* ── Derive intro paragraphs from about_us.content ──
     content may be plain text (newline-separated) or HTML.
     We strip HTML and split on double-newlines / sentence groups.
  ── */
  const introContent = aboutData?.content || "";
  const introText    = stripHtml(introContent).trim();

  return (
    <main className="sp">

      {/* ══════ 1. HERO ══════ */}
      <section className="sp-hero" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="sp-hero__overlay" />
        <div className="container sp-hero__body" ref={heroRef}>
          <div className={`sp-hero__label fade-in ${heroInView ? "visible" : ""}`}>
            OUR SERVICES
          </div>
          <h1
            className={`sp-hero__title fade-in ${heroInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.1s" }}
          >
            WE OFFER QUALITY<br />AUTO SOLUTIONS
          </h1>
        </div>
        <div className="sp-hero__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={13} />
          <span>Our Services</span>
        </div>
      </section>

      {/* ══════ 2. INTRO SPLIT + STATS ══════ */}
      <section className="sp-intro" ref={introRef}>
        <div className="container sp-intro__inner">
          <div className="sp-intro__img-col">
            <img
              src={introImg}
              alt="Noel Automotive site"
              loading="lazy"
              draggable="false"
              onDragStart={(e) => e.preventDefault()}
            />
          </div>
          <div className={`sp-intro__right fade-in ${introInView ? "visible" : ""}`}>
            <div className="sp-intro__quote">
              <div className="sp-intro__bar" />
              {loadingAbout ? (
                <p>Loading…</p>
              ) : introText ? (
                <p>{introText}</p>
              ) : (
                <p>
                  Welcome to Noel Automotive Repair — your trusted partner for quality
                  vehicle servicing and repairs. Our experienced technicians are dedicated
                  to keeping your vehicle performing at its best.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* shop image — same centered position as stats */}
        <Link to="/shop" className="sp-intro__shop-link">
          <img
            src={shopImg}
            alt="Shop Here"
            className="sp-intro__shop-img"
            draggable="false"
            onDragStart={(e) => e.preventDefault()}
          />
        </Link>
      </section>

      {/* ══════ 3. VALUES — 6-col full-width with webp images ══════ */}
      <section className="sp-values" ref={valRef}>
        <div className="sp-values__grid">
          {values.map((v, i) => (
            <div
              key={i}
              className={`sp-val fade-in ${valInView ? "visible" : ""}`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <h3 className="sp-val__title">{v.title}</h3>
              <div className="sp-val__img-wrap">
                <img
                  src={v.img}
                  alt={v.title}
                  className="sp-val__img"
                  draggable="false"
                  onDragStart={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                />
              </div>
              <p className="sp-val__desc">{v.desc}</p>
            </div>
          ))}
        </div>
        <div className="sp-values__stripe" aria-hidden="true" />
      </section>

      {/* ══════ 4. SERVICES GRID — from DB ══════ */}
      <section className="sp-svc" ref={svcRef}>
        <div className="container">
          <div className={`sp-svc__head fade-in ${svcInView ? "visible" : ""}`}>
            <div className="sp-svc__tag">OUR SERVICES</div>
            <h2 className="sp-svc__h2">What We Build, We Build Right</h2>
            <p className="sp-svc__sub">
              The best supply &amp; erect, technical solution with a quality product and
              exceptional service at the lowest available price
            </p>
          </div>

          {/* Loading state */}
          {svcLoading && (
            <p style={{ textAlign: "center", padding: "40px 0", color: "#888" }}>
              Loading services…
            </p>
          )}

          {/* Error state */}
          {svcError && (
            <p style={{ textAlign: "center", padding: "40px 0", color: "#f15a22" }}>
              {svcError}
            </p>
          )}

          {/* Services grid */}
          {!svcLoading && !svcError && (
            <div className="sp-svc__img-grid">
              {services.map((svc, i) => {
                const imgSrc = getServiceImage(svc);
                const icon   = svcIcons[i % svcIcons.length];
                const desc   = shortDesc(svc.description, 10);

                return (
                  <div
                    key={svc.id}
                    className={`sp-svc-img-card fade-in ${svcInView ? "visible" : ""}`}
                    style={{ transitionDelay: `${i * 0.07}s` }}
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    {/* photo */}
                    <div className="sp-svc-img-card__photo">
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt={svc.title}
                          loading="lazy"
                          draggable="false"
                          onDragStart={(e) => e.preventDefault()}
                        />
                      ) : (
                        <div className="sp-svc-img-card__no-img" />
                      )}
                      <div className="sp-svc-img-card__overlay" />
                      <div className="sp-svc-img-card__shield" aria-hidden="true" />
                    </div>

                    {/* icon box */}
                    <div className="sp-svc-img-card__icon-wrap">
                      <img
                        src={icon}
                        alt={svc.title}
                        className="sp-svc-img-card__icon"
                        draggable="false"
                        onDragStart={(e) => e.preventDefault()}
                      />
                    </div>

                    {/* text body */}
                    <div className="sp-svc-img-card__body">
                      <h3 className="sp-svc-img-card__title">{svc.title}</h3>
                      <p className="sp-svc-img-card__desc">{desc}</p>
                      <Link
                        to={`/services/${svc.slug}`}
                        className="sp-svc-img-card__link"
                      >
                        Read More
                        <span className="sp-svc-img-card__arrow">
                          <FiArrowRight size={13} />
                        </span>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

    </main>
  );
}